<script>
  import { onDestroy, onMount } from 'svelte';
  import { getApiBase } from './apiBase.js';
  import { generateTeacherPerformanceMonths } from './teacherPerformanceMonths.js';
  import TeacherCommentCoveragePopover from './TeacherCommentCoveragePopover.svelte';
  import ClassIncidentReport from './ClassIncidentReport.svelte';

  export let embedded = false;
  export let teacherId = null;
  let lastAutoLoadKey = null;

  // State for teachers
  let teachers = [];
  let teacherQuery = '';
  let filteredTeachers = [];
  let selectedTeacher = null;
  let showTeacherDropdown = false;

  // State for month selection
  let selectedMonth = '';
  let availableMonths = [];

  // State for submission
  let submitting = false;
  let submitError = null;
  let submitSuccess = false;
  let retentionData = null;
  let commentsData = null;
  let completionData = null;
  let surveyData = null;
  let paymentData = null;
  let attendanceData = null;
  let bonusData = null;

  // Expand state for classes with surveys
  let showAllSurveyClasses = false;
  
  // Expand state for other class sections
  let showAllPaymentClasses = false;
  let showAllAttendanceClasses = false;
  let showAllStudentAttendanceClasses = false;

  // Tab system
  let activeTab = 'individual'; // 'individual' or 'dashboard'
  
  // Dashboard data
  let allSnapshotsData = null;
  let dashboardLoading = false;
  let dashboardError = null;
  let dashboardMonth = '';
  let incidentRefreshKey = 0;
  
  // Recalculation state
  let recalculating = false;
  let recalculationError = null;
  let recalculationSuccess = false;
  let recalculationMessage = null;
  let recalculationStatus = null;
  let recalculationStatusPollId = null;
  let lastStatusCheckKey = null;
  const RECALC_AWAITING_MONTH_KEY = 'teacherSnapshotRecalcAwaitingMonth';
  const RECALC_DONE_MONTH_KEY = 'teacherSnapshotRecalcDoneMonth';
  const ACTIVE_RECALC_STATUSES = new Set(['started', 'already_running', 'running']);

  // Admin check
  let isUserSuper = null;

  const apiBase = getApiBase();

  // Get rating color class based on rating value (1-5)
  function getRatingColorClass(rating) {
    switch(rating) {
      case 1: return 'rating-1'; // red
      case 2: return 'rating-2'; // orange-red (between red and yellow)
      case 3: return 'rating-3'; // yellow
      case 4: return 'rating-4'; // yellow-green (between yellow and green)
      case 5: return 'rating-5'; // green
      default: return '';
    }
  }

  // Check if user is admin (superuser)
  async function checkIfUserIsAdmin() {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.warn('JWT token not found for admin check');
        return;
      }

      const response = await fetch(`${apiBase}/studentplayground/isSuperUser/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        isUserSuper = data.isUserSuper;
        console.log('Admin status:', isUserSuper);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  }

  // Fetch teachers using the same API as ClassDidNotHappenComponent
  async function fetchTeachers() {
    const url = `${apiBase}/studentplayground/api/get_google_calendar_ids/`;
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      submitError = 'JWT token not found. Please log in.';
      return;
    }
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      
      const data = await response.json();
      teachers = data.teachers || [];
      filteredTeachers = teachers;
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
      submitError = `Failed to fetch teachers: ${error.message}`;
    }
  }

  // Filter teachers as user types
  $: filteredTeachers = teacherQuery
    ? teachers.filter(t => t.name.toLowerCase().includes(teacherQuery.toLowerCase()))
    : teachers;
  $: showTeacherDropdown = teacherQuery && filteredTeachers.length > 0 && !selectedTeacher;

  // Embedded mode: force individual tab and auto-select teacher
  $: if (embedded && activeTab !== 'individual') {
    activeTab = 'individual';
  }

  $: if (embedded && teacherId && teachers.length) {
    const match = teachers.find(t => t.id === Number(teacherId));
    if (match && (!selectedTeacher || selectedTeacher.id !== match.id)) {
      selectTeacher(match);
    }
  }

  // On mount, fetch teachers, check admin status, and initialize months
  onMount(() => {
    fetchTeachers();
    checkIfUserIsAdmin();
    availableMonths = generateTeacherPerformanceMonths();

    if (embedded) {
      // Default to current month (if present in availableMonths)
      const now = new Date();
      const currentValue = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      if (!selectedMonth) {
        selectedMonth = availableMonths.find(m => m.value === currentValue)?.value || availableMonths[0]?.value || '';
      }
    }
  });

  function selectTeacher(teacher) {
    selectedTeacher = teacher;
    teacherQuery = teacher.name;
    showTeacherDropdown = false;
    // Clear any previous states
    submitError = null;
    submitSuccess = false;
  }

  function clearSelection() {
    selectedTeacher = null;
    teacherQuery = '';
    selectedMonth = '';
    submitError = null;
    submitSuccess = false;
    retentionData = null;
    commentsData = null;
    completionData = null;
    surveyData = null;
    paymentData = null;
    attendanceData = null;
  }

  // Submit selected teacher to dummy view
  async function submitTeacherSelection() {
    if (!selectedTeacher) {
      submitError = 'Please select a teacher first.';
      return;
    }

    if (!selectedMonth) {
      submitError = 'Please select a month first.';
      return;
    }

    submitting = true;
    submitError = null;
    submitSuccess = false;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      submitError = 'JWT token not found. Please log in.';
      submitting = false;
      return;
    }

    try {
      const response = await fetch(`${apiBase}/studentplayground/api/teacher_performance_rating/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: selectedTeacher.id,
          teacher_name: selectedTeacher.name,
          selected_month: selectedMonth,
          selected_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Teacher performance data received:', data);
        retentionData = data.retention_analysis;
        commentsData = data.comments_analysis;
        completionData = data.completion_analysis;
        surveyData = data.survey_analysis;
        paymentData = data.payment_analysis;
        attendanceData = data.attendance_analysis;
        bonusData = data.bonus_analysis;
        if (!embedded) {
          submitSuccess = true;
          setTimeout(() => {
            submitSuccess = false;
          }, 3000);
        }
      } else {
        const data = await response.json();
        submitError = data.error || 'Failed to submit teacher selection.';
      }
    } catch (error) {
      submitError = `Network error: ${error.message}`;
    } finally {
      submitting = false;
    }
  }

  // Embedded mode: auto-load when teacher + month are ready (and re-load on month change)
  $: if (embedded && selectedTeacher && selectedMonth && !submitting) {
    const key = `${selectedTeacher.id}:${selectedMonth}`;
    if (key !== lastAutoLoadKey) {
      lastAutoLoadKey = key;
      submitTeacherSelection();
    }
  }

  // Fetch all teacher payment snapshots for dashboard view
  async function fetchAllSnapshots() {
    if (!dashboardMonth) {
      dashboardError = 'Please select a month';
      return;
    }

    dashboardLoading = true;
    incidentRefreshKey += 1;
    dashboardError = null;
    allSnapshotsData = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      dashboardError = 'JWT token not found. Please log in.';
      dashboardLoading = false;
      return;
    }

    try {
      const url = `${apiBase}/studentplayground/api/all_teacher_payment_snapshots/?month=${dashboardMonth}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          allSnapshotsData = data;
          console.log('All snapshots loaded:', allSnapshotsData);
        } else {
          dashboardError = 'No snapshot data available';
        }
      } else {
        const errorData = await response.json();
        dashboardError = errorData.error || 'Failed to fetch snapshots';
      }
    } catch (error) {
      dashboardError = `Network error: ${error.message}`;
    } finally {
      dashboardLoading = false;
    }
  }

  // Recalculate all teacher snapshots for the selected month
  async function recalculateAllSnapshots() {
    if (!dashboardMonth) {
      recalculationError = 'Please select a month';
      return;
    }

    recalculating = true;
    recalculationError = null;
    recalculationSuccess = false;
    recalculationMessage = null;

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      recalculationError = 'JWT token not found. Please log in.';
      recalculating = false;
      return;
    }

    try {
      const url = `${apiBase}/studentplayground/api/recalculate_all_teacher_snapshots/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          month: dashboardMonth
        })
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok) {
        if (data?.success) {
          sessionStorage.setItem(RECALC_AWAITING_MONTH_KEY, dashboardMonth);
          recalculationSuccess = true;
          recalculationMessage = 'Calculating...';
          recalculationStatus = {
            ...data,
            status: ACTIVE_RECALC_STATUSES.has(data.status) ? 'running' : data.status,
            month: dashboardMonth
          };
          console.log('Recalculation request accepted:', data);

          if (ACTIVE_RECALC_STATUSES.has(data.status)) {
            startRecalculationStatusPolling();
            fetchRecalculationStatus();
          }
        } else {
          recalculationError = data?.error || 'Failed to start recalculation';
        }
      } else {
        recalculationError = data?.error || 'Failed to start recalculation';
      }
    } catch (error) {
      recalculationError = `Network error: ${error.message}`;
    } finally {
      recalculating = false;
    }
  }

  async function fetchRecalculationStatus() {
    if (!dashboardMonth || embedded || !isUserSuper) {
      return;
    }

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      return;
    }

    try {
      const url = `${apiBase}/studentplayground/api/recalculate_all_teacher_snapshots_status/?month=${dashboardMonth}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (!data?.success) {
        return;
      }

      recalculationStatus = data;

      if (ACTIVE_RECALC_STATUSES.has(data.status)) {
        recalculationSuccess = true;
        recalculationMessage = 'Calculating...';
        recalculationError = null;
        startRecalculationStatusPolling();
      } else if (data.status === 'completed') {
        stopRecalculationStatusPolling();
        const awaitingMonth = sessionStorage.getItem(RECALC_AWAITING_MONTH_KEY);
        if (awaitingMonth === data.month) {
          sessionStorage.removeItem(RECALC_AWAITING_MONTH_KEY);
          sessionStorage.setItem(RECALC_DONE_MONTH_KEY, data.month);
          window.location.reload();
          return;
        }

        recalculationSuccess = true;
        recalculationError = null;
        recalculationMessage = 'Done.';
      } else if (data.status === 'failed') {
        sessionStorage.removeItem(RECALC_AWAITING_MONTH_KEY);
        recalculationError = data.message || 'Recalculation failed.';
        recalculationSuccess = false;
        stopRecalculationStatusPolling();
      } else if (data.status === 'idle') {
        recalculationStatus = null;
        recalculationSuccess = false;
        recalculationMessage = null;
        stopRecalculationStatusPolling();
      }
    } catch (error) {
      console.warn('Failed to fetch recalculation status:', error);
    }
  }

  function startRecalculationStatusPolling() {
    if (recalculationStatusPollId) {
      return;
    }

    recalculationStatusPollId = setInterval(() => {
      fetchRecalculationStatus();
    }, 3000);
  }

  function stopRecalculationStatusPolling() {
    if (recalculationStatusPollId) {
      clearInterval(recalculationStatusPollId);
      recalculationStatusPollId = null;
    }
  }

  // Initialize dashboard month to current month when component mounts
  onMount(() => {
    const doneMonth = sessionStorage.getItem(RECALC_DONE_MONTH_KEY);
    const awaitingMonth = sessionStorage.getItem(RECALC_AWAITING_MONTH_KEY);

    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    dashboardMonth = doneMonth || awaitingMonth || currentMonth;

    if (doneMonth) {
      activeTab = 'dashboard';
      recalculationSuccess = true;
      recalculationMessage = 'Done.';
      sessionStorage.removeItem(RECALC_DONE_MONTH_KEY);
      fetchAllSnapshots();
    } else if (awaitingMonth) {
      activeTab = 'dashboard';
      recalculationSuccess = true;
      recalculationMessage = 'Calculating...';
      startRecalculationStatusPolling();
      fetchRecalculationStatus();
    }
  });

  onDestroy(() => {
    stopRecalculationStatusPolling();
  });

  $: if (isUserSuper && !embedded && activeTab === 'dashboard' && dashboardMonth) {
    const statusKey = `${activeTab}:${dashboardMonth}`;
    if (statusKey !== lastStatusCheckKey) {
      lastStatusCheckKey = statusKey;
      fetchRecalculationStatus();
    }
  }

  // Dashboard: teachers with at least 1 class, sorted alphabetically by name
  $: displayedSnapshots = allSnapshotsData?.snapshots
    ? [...allSnapshotsData.snapshots]
        .filter((s) => (s.total_classes || 0) > 0)
        .sort((a, b) => (a.teacher_name || '').localeCompare(b.teacher_name || '', undefined, { sensitivity: 'base' }))
    : [];

  // Summary for displayed rows only (filtered list)
  $: displayedSummary = displayedSnapshots.length
    ? {
        total_teachers: displayedSnapshots.length,
        grand_total_payment: displayedSnapshots.reduce((sum, s) => sum + (parseFloat(s.total_eur) || 0), 0).toFixed(2),
        total_classes: displayedSnapshots.reduce((sum, s) => sum + (s.total_classes || 0), 0),
        average_per_teacher: (displayedSnapshots.reduce((sum, s) => sum + (parseFloat(s.total_eur) || 0), 0) / displayedSnapshots.length).toFixed(2),
      }
    : null;
</script>

<div class="teacher-performance-rating">
  <h2>Teacher Performance Rating</h2>
  <p class="description">Select a teacher and month to view their performance ratings and feedback for that specific period.</p>

  <!-- Tab Navigation -->
  {#if isUserSuper && !embedded}
    <div class="tab-navigation">
      <button 
        class="tab-button" 
        class:active={activeTab === 'individual'}
        on:click={() => activeTab = 'individual'}
      >
        Individual Teacher
      </button>
      <button 
        class="tab-button" 
        class:active={activeTab === 'dashboard'}
        on:click={() => activeTab = 'dashboard'}
      >
        All Teachers Dashboard
      </button>
    </div>
  {/if}

  <!-- Individual Teacher Tab -->
  {#if activeTab === 'individual'}
  <div class="teacher-selection">
    {#if !embedded}
      <label for="teacher-input"><strong>Select Teacher:</strong></label>
      <div class="input-container">
        <input
          id="teacher-input"
          type="text"
          placeholder="Search teacher by name..."
          bind:value={teacherQuery}
          on:input={() => {
            if (selectedTeacher && teacherQuery !== selectedTeacher.name) {
              selectedTeacher = null;
              showTeacherDropdown = true;
            }
          }}
          autocomplete="off"
        />
        
        {#if showTeacherDropdown}
          <ul class="autocomplete-list">
            {#each filteredTeachers.slice(0, 8) as teacher}
              <li>
                <button
                  type="button"
                  on:click={() => selectTeacher(teacher)}
                  on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
                >
                  <div class="teacher-option">
                    <span class="teacher-name">{teacher.name}</span>
                    {#if teacher.email}
                      <span class="teacher-email">{teacher.email}</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {:else if teacherId && !selectedTeacher}
      <div class="loading">Loading teacher...</div>
    {/if}

    <div class="month-selection">
      <label for="month-select"><strong>Select Month:</strong></label>
      <select
        id="month-select"
        bind:value={selectedMonth}
        class="month-dropdown"
      >
        <option value="">Choose a month...</option>
        {#each availableMonths as month}
          <option value={month.value}>{month.label}</option>
        {/each}
      </select>
    </div>
    
    {#if selectedTeacher}
      <div class="selected-teacher">
        <div class="selected-teacher-info">
          <h3>Selected Teacher</h3>
          <p><strong>Name:</strong> {selectedTeacher.name}</p>
          {#if selectedTeacher.email}
            <p><strong>Email:</strong> {selectedTeacher.email}</p>
          {/if}
          <p><strong>ID:</strong> {selectedTeacher.id}</p>
          {#if selectedMonth}
            <p><strong>Selected Month:</strong> {availableMonths.find(m => m.value === selectedMonth)?.label}</p>
          {/if}
        </div>
        
        <div class="action-buttons">
          {#if !embedded}
            <button 
              type="button" 
              class="btn-submit"
              on:click={submitTeacherSelection}
              disabled={submitting || !selectedMonth}
            >
              {submitting ? 'Processing...' : 'View Performance Rating'}
            </button>
          {:else}
            <div class="embedded-auto">
              {#if submitting}
                <span>Loading performance…</span>
              {:else}
                <span>Auto-refresh enabled</span>
              {/if}
            </div>
          {/if}
          {#if !embedded}
            <button 
              type="button" 
              class="btn-clear" 
              on:click={clearSelection}
              disabled={submitting}
            >
              Clear Selection
            </button>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  {#if submitError}
    <div class="error-message">{submitError}</div>
  {/if}

  {#if isUserSuper && selectedTeacher && selectedMonth}
    <ClassIncidentReport month={selectedMonth} teacherId={selectedTeacher.id} />
  {/if}

  {#if submitSuccess}
    <div class="success-message">
      Teacher performance data retrieved successfully!
    </div>
  {/if}

  {#if paymentData}
    <div class="retention-container">
      <h3>Teacher Payment Analysis</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Total Payment: €{paymentData.payment_analysis.total_payment}</strong>
        </div>
        <p>({paymentData.payment_analysis.total_completed_classes} completed, {paymentData.payment_analysis.total_upcoming_classes} upcoming | Average €{paymentData.payment_analysis.average_payment_per_class} per class)</p>
        <p>Period: {new Date(paymentData.period.start_date).toLocaleDateString()} - {new Date(paymentData.period.end_date).toLocaleDateString()}</p>
      </div>

      {#if paymentData.payment_analysis.total_month_classes > 0}
        <!-- Class Summary Section -->
        <div class="comments-section">
          <h4>Class Summary</h4>
          <div class="payment-breakdown-container">
            <div class="payment-section">
              <h5>Class Types</h5>
              <ul class="class-count-list">
                <li><strong>Individual Classes:</strong> {paymentData.payment_analysis.payment_summary.individual_classes.count} classes</li>
                <li><strong>Pair Classes:</strong> {paymentData.payment_analysis.payment_summary.pair_classes.count} classes</li>
                <li><strong>Group Classes:</strong> {paymentData.payment_analysis.payment_summary.group_classes.count} classes</li>
              </ul>
            </div>
            
            <div class="payment-section">
              <h5>Special Categories</h5>
              <ul class="class-count-list">
                <li><strong>Special Conditions Classes:</strong> {paymentData.payment_analysis.payment_summary.ib_classes.count} classes</li>
                {#if paymentData.payment_analysis.payment_summary.free_classes.count > 0}
                  <li><strong>Free Classes:</strong> {paymentData.payment_analysis.payment_summary.free_classes.count} classes</li>
                {/if}
                <li class="total-classes"><strong>Total Classes: {paymentData.payment_analysis.total_completed_classes + paymentData.payment_analysis.total_upcoming_classes}</strong></li>
              </ul>
            </div>
          </div>
          <div class="payment-total-only">
            <p><strong>Total Payment: €{paymentData.payment_analysis.total_payment}</strong></p>
          </div>
        </div>

        <!-- Class-by-Class Payment Details -->
        <div class="comments-section">
          <h4>Class-by-Class Payment Details</h4>
          <div class="attendance-details">
            {#each paymentData.payment_analysis.class_payment_details
              .sort((a, b) => new Date(a.class_time) - new Date(b.class_time))
              .slice(0, showAllPaymentClasses ? paymentData.payment_analysis.class_payment_details.length : 10) as classDetail}
              <div class="attendance-item {classDetail.status === 'upcoming' ? 'upcoming-class' : ''}">
                <div class="attendance-header">
                  <strong>{classDetail.class_name}</strong>
                  <div class="class-status-payment">
                    <span class="class-status {classDetail.status}">{classDetail.status}</span>
                    <span class="attendance-rate {classDetail.is_free_class ? 'free-class' : 'paid-class'}">
                      €{classDetail.payment}
                    </span>
                  </div>
                </div>
                <div class="attendance-stats">
                  <small>{classDetail.display_time}</small>
                  <small>Students: {classDetail.students} ({classDetail.student_count_category})</small>
                  {#if classDetail.special_conditions && classDetail.special_conditions.length > 0}
                    <div class="special-conditions-container">
                      {#each classDetail.special_conditions as condition}
                        {#if condition === 'IB'}
                          <small class="special-condition ib-condition">IB (+€5)</small>
                        {:else if condition === 'Special Needs'}
                          <small class="special-condition special-needs-condition">Special Needs (+€3)</small>
                        {:else if condition === 'Lithuanian Second Language'}
                          <small class="special-condition lithuanian-condition">Lithuanian L2 (+€3)</small>
                        {/if}
                      {/each}
                      {#if classDetail.special_conditions_surcharge > 0}
                        <small class="total-surcharge">Total surcharge: +€{classDetail.special_conditions_surcharge}</small>
                      {/if}
                    </div>
                  {/if}
                  {#if classDetail.is_free_class}
                    <small class="free-reason">Free: First class for non-retained students</small>
                  {/if}
                  {#if classDetail.has_first_time_students && classDetail.first_time_students.length > 0}
                    <small class="first-time">First-time: {classDetail.first_time_students.join(', ')}</small>
                  {/if}
                  {#if classDetail.has_retained_students && classDetail.retained_students.length > 0}
                    <small class="retained">Retained: {classDetail.retained_students.join(', ')}</small>
                  {/if}
                </div>
              </div>
            {/each}
            {#if paymentData.payment_analysis.class_payment_details.length > 10}
              <div class="more-classes">
                <button 
                  class="expand-button" 
                  on:click={() => showAllPaymentClasses = !showAllPaymentClasses}
                >
                  {showAllPaymentClasses 
                    ? 'Show Less' 
                    : `Show ${paymentData.payment_analysis.class_payment_details.length - 10} More Classes`}
                </button>
              </div>
            {/if}
          </div>
        </div>
      {:else}
        <div class="no-data">
          <p>No classes found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if commentsData}
    <div class="retention-container">
      <h3>Student Comments About Teacher</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Comments: {commentsData.comments_analysis.total_comments}</strong>
        </div>
        <p>Student feedback from {new Date(commentsData.month_period.start_date).toLocaleDateString()} - {new Date(commentsData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if commentsData.comments_analysis.total_comments > 0}
        <!-- Student Rating Average Section -->
        <div class="comments-section">
          <h4>Student Rating</h4>
          <div class="student-rating-summary">
            <div class="rating-display">
              <div class="rating-value">
                <strong>{commentsData.comments_analysis.rating_averages.general.average}/5</strong>
              </div>
              <div class="rating-details">
                <p>Average Class Rating</p>
                <p>({commentsData.comments_analysis.rating_averages.general.rated_comments} student ratings)</p>
              </div>
            </div>
            <div class="rating-bar-container">
              <div class="metric-bar">
                <div class="metric-fill" style="width: {(commentsData.comments_analysis.rating_averages.general.average / 5) * 100}%"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- All Student Comments Section - Always visible for admins -->
        {#if commentsData.comments_analysis.sample_comments.length > 0}
          <div class="comments-section">
            <h4>All Student Comments</h4>
            <div class="sample-comments">
              {#each commentsData.comments_analysis.sample_comments as comment}
                <div class="comment-item">
                  <strong>{comment.student}:</strong>
                  <span>"{comment.text}"</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>No student comments found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if bonusData}
    <div class="retention-container">
      <h3>Bonus Eligibility Analysis</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Bonus Eligibility: {bonusData.eligible_for_bonus ? 'Eligible' : 'Not Eligible'}</strong>
        </div>
         <p>Criteria: {bonusData.criteria?.criteria_met || 0}/{bonusData.criteria?.total_criteria || 0} met</p>
         <p>Period: {new Date(bonusData.month_period?.start_date || bonusData.details?.month_period?.start_date).toLocaleDateString()} - {new Date(bonusData.month_period?.end_date || bonusData.details?.month_period?.end_date).toLocaleDateString()}</p>
      </div>

      {#if bonusData.eligible_for_bonus}
        <div class="bonus-eligibility-banner">
          <div class="bonus-icon">🎉</div>
          <div class="bonus-message">
            <h4>Congratulations! You are eligible for bonus</h4>
            <p>Each paid class this month receives <strong>+€{bonusData.amount_per_class ?? 2} bonus</strong></p>
             {#if bonusData.month_period || bonusData.details?.month_period}
               <small>Period: {new Date(bonusData.month_period?.start_date || bonusData.details?.month_period?.start_date).toLocaleDateString()} - {new Date(bonusData.month_period?.end_date || bonusData.details?.month_period?.end_date).toLocaleDateString()}</small>
             {/if}
          </div>
        </div>
      {:else}
        <div class="bonus-eligibility-banner not-eligible">
          <div class="bonus-icon">📊</div>
          <div class="bonus-message">
            <h4>Bonus criteria not met</h4>
            <p>When all criteria are met, you will receive <strong>+€{bonusData.amount_per_class ?? 2} bonus</strong> for each paid class</p>
             {#if bonusData.month_period || bonusData.details?.month_period}
               <small>Period: {new Date(bonusData.month_period?.start_date || bonusData.details?.month_period?.start_date).toLocaleDateString()} - {new Date(bonusData.month_period?.end_date || bonusData.details?.month_period?.end_date).toLocaleDateString()}</small>
             {/if}
          </div>
        </div>
      {/if}

       <!-- Detailed Criteria Breakdown -->
       {#if bonusData.criteria?.criteria_breakdown && bonusData.criteria.criteria_breakdown.length > 0}
         <div class="comments-section">
           <h4>Criteria Breakdown</h4>
           <div class="criteria-breakdown">
             {#each bonusData.criteria.criteria_breakdown as criterion}
            <div class="criterion-item">
              <div class="criterion-name">
                <strong>{criterion.name}</strong>
                <span class="criterion-status {criterion.met ? 'met' : 'not-met'}">
                  {criterion.met ? '✓' : '✗'}
                </span>
              </div>
              <div class="criterion-details">
                <p>{criterion.description}</p>
                <p class="criterion-value">
                  Current: {criterion.current_value} / Required: {criterion.required_value}
                </p>
              </div>
             </div>
           {/each}
         </div>
       </div>
       {/if}
     </div>
   {/if}

  {#if surveyData}
    <div class="retention-container">
      <h3>Teacher Survey Submission Rate</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Survey Submission Rate: {surveyData.survey_analysis.submission_rate_percentage}%</strong>
        </div>
        <p>({surveyData.survey_analysis.surveys_submitted} out of {surveyData.survey_analysis.total_classes} classes have teacher surveys)</p>
        <p>Period: {new Date(surveyData.month_period.start_date).toLocaleDateString()} - {new Date(surveyData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if surveyData.survey_analysis.total_classes > 0}
        <!-- Progress Bar -->
        <div class="comments-section">
          <h4>Submission Overview</h4>
          <div class="metric-item">
            <strong>Overall Compliance</strong>
            <div class="metric-bar">
              <div class="metric-fill" style="width: {surveyData.survey_analysis.submission_rate_percentage}%"></div>
            </div>
            <small>{surveyData.survey_analysis.surveys_submitted} submitted, {surveyData.survey_analysis.classes_missing_surveys.length} missing</small>
          </div>
        </div>

        <!-- Classes with Surveys -->
        {#if surveyData.survey_analysis.classes_with_surveys.length > 0}
          <div class="comments-section">
            <h4>Classes with Teacher Surveys ({surveyData.survey_analysis.classes_with_surveys.length})</h4>
            <div class="sample-comments">
              {#each surveyData.survey_analysis.classes_with_surveys
                .sort((a, b) => new Date(a.class_time) - new Date(b.class_time))
                .slice(0, showAllSurveyClasses ? surveyData.survey_analysis.classes_with_surveys.length : 5) as classItem}
                <div class="comment-item">
                  <strong>{classItem.class_name}</strong>
                  <div class="class-details">
                    <small>{new Date(classItem.class_time).toLocaleDateString()} - {classItem.class_display_time}</small>
                    <small>Students: {classItem.students}</small>
                    {#if classItem.note_text}
                      <p>"{classItem.note_text}"</p>
                    {/if}
                    <div class="survey-ratings">
                      {#if classItem.effort_rating > 0}
                        <span class="rating-badge {getRatingColorClass(classItem.effort_rating)}">Effort: {classItem.effort_rating}/5</span>
                      {/if}
                      {#if classItem.learned_rating > 0}
                        <span class="rating-badge {getRatingColorClass(classItem.learned_rating)}">Learning: {classItem.learned_rating}/5</span>
                      {/if}
                      {#if classItem.general_rating > 0}
                        <span class="rating-badge {getRatingColorClass(classItem.general_rating)}">General: {classItem.general_rating}/5</span>
                      {/if}
                    </div>
                  </div>
                </div>
              {/each}
              {#if surveyData.survey_analysis.classes_with_surveys.length > 5}
                <div class="more-classes">
                  <button 
                    class="expand-button" 
                    on:click={() => showAllSurveyClasses = !showAllSurveyClasses}
                  >
                    {showAllSurveyClasses 
                      ? 'Show Less' 
                      : `Show ${surveyData.survey_analysis.classes_with_surveys.length - 5} More Classes`}
                  </button>
                </div>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Classes Missing Surveys -->
        {#if surveyData.survey_analysis.classes_missing_surveys.length > 0}
          <div class="comments-section">
            <h4>Classes Missing Teacher Surveys ({surveyData.survey_analysis.classes_missing_surveys.length})</h4>
            <div class="missing-surveys">
              {#each surveyData.survey_analysis.classes_missing_surveys
                .sort((a, b) => new Date(a.class_time) - new Date(b.class_time)) as classItem}
                <div class="missing-item">
                  <strong>{classItem.class_name}</strong>
                  <div class="class-details">
                    <small>{new Date(classItem.class_time).toLocaleDateString()} - {classItem.class_display_time}</small>
                    <small>Students: {classItem.students}</small>
                    <span class="missing-badge">Survey Missing</span>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>No classes found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if commentsData}
    <div class="retention-container">
      <h3>Student Comments About Teacher</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Comments: {commentsData.comments_analysis.total_comments}</strong>
        </div>
        <p>Student feedback from {new Date(commentsData.month_period.start_date).toLocaleDateString()} - {new Date(commentsData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if commentsData.comments_analysis.total_comments > 0}
        <!-- Student Rating Average Section -->
        <div class="comments-section">
          <h4>Student Rating</h4>
          <div class="student-rating-summary">
            <div class="rating-display">
              <div class="rating-value">
                <strong>{commentsData.comments_analysis.rating_averages.general.average}/5</strong>
              </div>
              <div class="rating-details">
                <p>Average Class Rating</p>
                <p>({commentsData.comments_analysis.rating_averages.general.rated_comments} student ratings)</p>
              </div>
            </div>
            <div class="rating-bar-container">
              <div class="metric-bar">
                <div class="metric-fill" style="width: {(commentsData.comments_analysis.rating_averages.general.average / 5) * 100}%"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- All Student Comments Section - Always visible for admins -->
        {#if commentsData.comments_analysis.sample_comments.length > 0}
          <div class="comments-section">
            <h4>All Student Comments</h4>
            <div class="sample-comments">
              {#each commentsData.comments_analysis.sample_comments as comment}
                <div class="comment-item">
                  <strong>{comment.student}:</strong>
                  <span>"{comment.text}"</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>No student comments found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if attendanceData && isUserSuper}
    <div class="retention-container">
      <h3>Teacher Attendance Analysis</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Teacher Attendance Rate: {attendanceData.attendance_analysis.attendance_rate_percentage}%</strong>
        </div>
        <p>({attendanceData.attendance_analysis.attended_classes} out of {attendanceData.attendance_analysis.total_scheduled} classes attended)</p>
        <p>Period: {new Date(attendanceData.month_period.start_date).toLocaleDateString()} - {new Date(attendanceData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if attendanceData.attendance_analysis.total_scheduled > 0}
        <!-- Main Metrics Section -->
        <div class="month-comparison">
          <div class="month-data">
            <h4>Teacher Attendance Summary</h4>
            <ul>
              <li><strong>Total Scheduled:</strong> {attendanceData.attendance_analysis.total_scheduled}</li>
              <li><strong>Classes Attended:</strong> {attendanceData.attendance_analysis.attended_classes}</li>
              <li><strong>Late Arrivals:</strong> {attendanceData.attendance_analysis.late_classes}</li>
              <li><strong>No-Shows:</strong> {attendanceData.attendance_analysis.no_show_classes}</li>
            </ul>
          </div>
          
          <div class="month-data">
            <h4>Attendance Performance</h4>
            <ul>
              <li><strong>On-Time Classes:</strong> {attendanceData.attendance_analysis.on_time_classes}</li>
              <li><strong>Attendance Rate:</strong> {attendanceData.attendance_analysis.attendance_rate_percentage}%</li>
              <li><strong>Punctuality Rate:</strong> {attendanceData.attendance_analysis.punctuality_rate_percentage}%</li>
            </ul>
          </div>
        </div>

        <!-- Visual Progress Bars -->
        <div class="comments-section">
          <h4>Attendance Overview</h4>
          <div class="metrics-grid">
            <div class="metric-item">
              <strong>Teacher Attendance Rate</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {attendanceData.attendance_analysis.attendance_rate_percentage}%"></div>
              </div>
              <small>{attendanceData.attendance_analysis.attended_classes} out of {attendanceData.attendance_analysis.total_scheduled} classes attended</small>
            </div>
            
            <div class="metric-item">
              <strong>Teacher Punctuality Rate</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {attendanceData.attendance_analysis.punctuality_rate_percentage}%"></div>
              </div>
              <small>{attendanceData.attendance_analysis.on_time_classes} out of {attendanceData.attendance_analysis.total_scheduled} classes on time ({attendanceData.attendance_analysis.late_classes} late arrivals)</small>
            </div>
          </div>
        </div>

        <!-- Class-by-Class Teacher Attendance Details -->
        {#if attendanceData.attendance_analysis.class_attendance_details && attendanceData.attendance_analysis.class_attendance_details.length > 0}
          <div class="comments-section">
            <h4>Class-by-Class Teacher Attendance Details</h4>
            <div class="attendance-details">
              {#each attendanceData.attendance_analysis.class_attendance_details
                .sort((a, b) => new Date(a.class_time) - new Date(b.class_time))
                .slice(0, showAllAttendanceClasses ? attendanceData.attendance_analysis.class_attendance_details.length : 10) as classDetail}
                <div class="attendance-item {classDetail.attendance_status === 'no_show' ? 'no-show-item' : classDetail.attendance_status === 'late' ? 'late-item' : 'on-time-item'}">
                  <div class="attendance-header">
                    <strong>{classDetail.class_name}</strong>
                    <div class="class-status-payment">
                      <span class="attendance-status {classDetail.attendance_status}">
                        {classDetail.attendance_status === 'no_show' ? 'No Show' : 
                         classDetail.attendance_status === 'late' ? `Late (${classDetail.minutes_late}min)` : 
                         'On Time'}
                      </span>
                    </div>
                  </div>
                  <div class="attendance-stats">
                    <small>Scheduled: {classDetail.class_time}</small>
                    <small>Attended: {classDetail.attendance_time}</small>
                    <small>Display: {classDetail.display_time}</small>
                  </div>
                </div>
              {/each}
              {#if attendanceData.attendance_analysis.class_attendance_details.length > 10}
                <div class="more-classes">
                  <button 
                    class="expand-button" 
                    on:click={() => showAllAttendanceClasses = !showAllAttendanceClasses}
                  >
                    {showAllAttendanceClasses 
                      ? 'Show Less' 
                      : `Show ${attendanceData.attendance_analysis.class_attendance_details.length - 10} More Classes`}
                  </button>
                </div>
              {/if}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>No scheduled classes found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  {#if retentionData}
    <div class="retention-container">
      <h3>Teacher Retention Analysis</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Retention Rate: {retentionData.retention.retention_rate_percentage}%</strong>
        </div>
        <p>({retentionData.retention.retained_students} out of {retentionData.current_month.unique_students} students continuing to next month)</p>
      </div>

      <div class="month-comparison">
        <div class="month-data">
          <h4>Current Month</h4>
          <ul>
            <li><strong>Unique Students:</strong> {retentionData.current_month.unique_students}</li>
            <li><strong>Total Classes:</strong> {retentionData.current_month.total_classes}</li>
            <li><strong>Period:</strong> {new Date(retentionData.current_month.start_date).toLocaleDateString()} - {new Date(retentionData.current_month.end_date).toLocaleDateString()}</li>
          </ul>
        </div>
        
        <div class="month-data">
          <h4>Next Month</h4>
          <ul>
            <li><strong>Unique Students:</strong> {retentionData.next_month.unique_students}</li>
            <li><strong>Total Classes:</strong> {retentionData.next_month.total_classes}</li>
            <li><strong>Period:</strong> {new Date(retentionData.next_month.start_date).toLocaleDateString()} - {new Date(retentionData.next_month.end_date).toLocaleDateString()}</li>
          </ul>
        </div>
      </div>

      {#if retentionData.retention.retained_students_details.length > 0}
        <div class="student-details">
          <h4>Retained Students ({retentionData.retention.retained_students})</h4>
          <ul class="student-list">
            {#each retentionData.retention.retained_students_details as student}
              <li>{student.name || student.username}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if retentionData.retention.lost_students_details.length > 0}
        <div class="student-details">
          <h4>Students Not Continuing ({retentionData.retention.lost_students_details.length})</h4>
          <ul class="student-list">
            {#each retentionData.retention.lost_students_details as student}
              <li>{student.name || student.username}</li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>
  {/if}

  {#if completionData}
    <div class="retention-container">
      <h3>Class Completion & Punctuality</h3>
      
      <div class="retention-summary">
        <div class="retention-rate">
          <strong>Operational Efficiency: {completionData.completion_analysis.operational_efficiency.overall_score}%</strong>
        </div>
        <p>Combined completion rate and punctuality score for {new Date(completionData.month_period.start_date).toLocaleDateString()} - {new Date(completionData.month_period.end_date).toLocaleDateString()}</p>
      </div>

      {#if completionData.completion_analysis.total_scheduled > 0}
        <!-- Main Metrics Section -->
        <div class="month-comparison">
          <div class="month-data">
            <h4>Teacher Performance</h4>
            <ul>
              <li><strong>Total Scheduled:</strong> {completionData.completion_analysis.total_scheduled}</li>
              <li><strong>Teacher Completed:</strong> {completionData.completion_analysis.teacher_completed_classes}</li>
              <li><strong>Teacher Cancelled:</strong> {completionData.completion_analysis.teacher_cancelled_classes}</li>
              <li><strong>Teacher Completion Rate:</strong> {completionData.completion_analysis.teacher_completion_rate_percentage}%</li>
            </ul>
          </div>
          
          <div class="month-data">
            <h4>Payment & Responsibility</h4>
            <ul>
              <li><strong>Payment Eligible Classes:</strong> {completionData.completion_analysis.payment_eligible_classes.total_eligible}</li>
              <li><strong>Student Cancellations:</strong> {completionData.completion_analysis.responsibility_breakdown.student_cancellations}</li>
              <li><strong>Classes Actually Held:</strong> {completionData.completion_analysis.classes_actually_held}</li>
              <li><strong>Actual Completion Rate:</strong> {completionData.completion_analysis.actual_completion_rate_percentage}%</li>
            </ul>
          </div>
          
          <div class="month-data">
            <h4>Punctuality</h4>
            <ul>
              <li><strong>On-Time Classes:</strong> {completionData.completion_analysis.punctuality.on_time_classes}</li>
              <li><strong>Total Late Incidents:</strong> {completionData.completion_analysis.punctuality.total_late_incidents}</li>
              <li><strong>Formal Late Reports:</strong> {completionData.completion_analysis.punctuality.formal_late_incidents}</li>
              <li><strong>Student-Reported Late:</strong> {completionData.completion_analysis.punctuality.student_reported_late}</li>
              <li><strong>Punctuality Rate:</strong> {completionData.completion_analysis.punctuality.punctuality_rate_percentage}%</li>
            </ul>
          </div>
        </div>

        <!-- Student Attendance Section -->
        <div class="comments-section">
          <h4>Student Attendance Overview</h4>
          <div class="month-comparison">
            <div class="month-data">
              <h5>Attendance Summary</h5>
              <ul>
                <li><strong>Classes with Students:</strong> {completionData.completion_analysis.student_attendance.total_classes_with_students}</li>
                <li><strong>Classes with Attendance:</strong> {completionData.completion_analysis.student_attendance.classes_with_attendance}</li>
                <li><strong>Classes with No-Shows:</strong> {completionData.completion_analysis.student_attendance.classes_with_no_attendance}</li>
              </ul>
            </div>
            
            <div class="month-data">
              <h5>Attendance Rates</h5>
              <ul>
                <li><strong>Student Attendance Rate:</strong> {completionData.completion_analysis.student_attendance.student_attendance_rate_percentage}%</li>
                <li><strong>Student No-Show Rate:</strong> {completionData.completion_analysis.student_attendance.student_no_show_rate}%</li>
                <li><strong>Total Attendances:</strong> {completionData.completion_analysis.student_attendance.total_actual_attendances}/{completionData.completion_analysis.student_attendance.total_expected_attendances}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Visual Progress Bars -->
        <div class="comments-section">
          <h4>Performance Overview</h4>
          <div class="metrics-grid">
            <div class="metric-item">
              <strong>Teacher Completion Rate</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.teacher_completion_rate_percentage}%"></div>
              </div>
              <small>{completionData.completion_analysis.teacher_completed_classes} out of {completionData.completion_analysis.total_scheduled} classes completed by teacher</small>
            </div>
            
            <div class="metric-item">
              <strong>Punctuality Rate</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.punctuality.punctuality_rate_percentage}%"></div>
              </div>
              <small>{completionData.completion_analysis.punctuality.on_time_classes} out of {completionData.completion_analysis.total_scheduled} classes on time ({completionData.completion_analysis.punctuality.total_late_incidents} late incidents)</small>
            </div>
            
            <div class="metric-item">
              <strong>Student Attendance Rate</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {completionData.completion_analysis.student_attendance.student_attendance_rate_percentage}%"></div>
              </div>
              <small>{completionData.completion_analysis.student_attendance.total_actual_attendances} out of {completionData.completion_analysis.student_attendance.total_expected_attendances} expected attendances</small>
            </div>
          </div>
        </div>

        <!-- Student Attendance Details -->
        {#if completionData.completion_analysis.student_attendance_details && completionData.completion_analysis.student_attendance_details.length > 0}
          <div class="comments-section">
            <h4>Class-by-Class Attendance Details</h4>
            <div class="attendance-details">
              {#each completionData.completion_analysis.student_attendance_details
                .sort((a, b) => new Date(a.class_time) - new Date(b.class_time))
                .slice(0, showAllStudentAttendanceClasses ? completionData.completion_analysis.student_attendance_details.length : 10) as classDetail}
                <div class="attendance-item">
                  <div class="attendance-header">
                    <strong>{classDetail.class_name}</strong>
                    <span class="attendance-rate">{classDetail.attendance_rate.toFixed(1)}%</span>
                  </div>
                  <div class="attendance-stats">
                    <small>{classDetail.display_time}</small>
                    <small>{classDetail.attending_students}/{classDetail.enrolled_students} students attended</small>
                    {#if classDetail.no_show_students > 0}
                      <small class="no-show">({classDetail.no_show_students} no-shows)</small>
                    {/if}
                  </div>
                </div>
              {/each}
              {#if completionData.completion_analysis.student_attendance_details.length > 10}
                <div class="more-classes">
                  <button 
                    class="expand-button" 
                    on:click={() => showAllStudentAttendanceClasses = !showAllStudentAttendanceClasses}
                  >
                    {showAllStudentAttendanceClasses 
                      ? 'Show Less' 
                      : `Show ${completionData.completion_analysis.student_attendance_details.length - 10} More Classes`}
                  </button>
                </div>
              {/if}
            </div>
          </div>
        {/if}

        <!-- Weekly Trends if available -->
        {#if Object.keys(completionData.completion_analysis.weekly_trends || {}).length > 0}
          <div class="comments-section">
            <h4>Weekly Trends</h4>
            <div class="weekly-trends">
              {#each Object.entries(completionData.completion_analysis.weekly_trends) as [week, data]}
                <div class="week-item">
                  <strong>Week {week.split('_')[1]}</strong>
                  <div class="week-stats">
                    <span>Completion: {data.completion_rate.toFixed(1)}%</span>
                    <span>Punctuality: {data.punctuality_rate.toFixed(1)}%</span>
                    {#if data.total_late_incidents > 0}
                      <span class="late-incidents">Late: {data.formal_late || 0} formal, {data.student_reported_late || 0} student</span>
                    {/if}
                  </div>
                  <small>{data.period}</small>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        <!-- Student feedback is separate from formal responsibility incidents. -->
        {#if completionData.completion_analysis.punctuality.student_late_details?.length > 0}
                <div class="incident-details">
                  <h5>Student-Reported Punctuality Issues:</h5>
                  {#each completionData.completion_analysis.punctuality.student_late_details.slice(0, 5) as report}
                    <div class="incident-item student-feedback">
                      <strong>Class: {report.class_id}</strong>
                      <small>Reported by: {report.student_name}</small>
                      {#if report.note_text !== 'No additional comment'}
                        <span>Student comment: "{report.note_text}"</span>
                      {/if}
                      <div class="rating-info">
                        {#if report.effort_rating > 0}
                          <small class="rating-badge {getRatingColorClass(report.effort_rating)}">Effort rating: {report.effort_rating}/5</small>
                        {/if}
                        {#if report.learned_rating > 0}
                          <small class="rating-badge {getRatingColorClass(report.learned_rating)}">Learning rating: {report.learned_rating}/5</small>
                        {/if}
                        {#if report.general_rating > 0}
                          <small class="rating-badge {getRatingColorClass(report.general_rating)}">General rating: {report.general_rating}/5</small>
                        {/if}
                        <small class="punctuality-issue">On-time: No</small>
                      </div>
                    </div>
                  {/each}
                  {#if completionData.completion_analysis.punctuality.student_late_details.length > 5}
                    <div class="more-reports">
                      <small>... and {completionData.completion_analysis.punctuality.student_late_details.length - 5} more student reports</small>
                    </div>
                  {/if}
                </div>
              {/if}
      {:else}
        <div class="no-data">
          <p>No scheduled classes found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}
  {/if}
  <!-- End Individual Teacher Tab -->

  <!-- All Teachers Dashboard Tab -->
  {#if activeTab === 'dashboard' && isUserSuper}
    <div class="dashboard-tab">
      <div class="dashboard-controls">
        <label for="dashboard-month-select"><strong>Select Month:</strong></label>
        <div class="dashboard-month-selector">
          <select
            id="dashboard-month-select"
            bind:value={dashboardMonth}
            class="month-dropdown"
          >
            {#each availableMonths as month}
              <option value={month.value}>{month.label}</option>
            {/each}
          </select>
          <button 
            class="btn-load-dashboard"
            on:click={fetchAllSnapshots}
            disabled={dashboardLoading || recalculating}
          >
            {dashboardLoading ? 'Loading...' : 'Load Dashboard'}
          </button>
          {#if allSnapshotsData}
            <button 
              class="btn-recalculate-dashboard"
              on:click={recalculateAllSnapshots}
              disabled={dashboardLoading || recalculating || ACTIVE_RECALC_STATUSES.has(recalculationStatus?.status) || !dashboardMonth}
              title="Recalculate all teacher snapshots for the selected month"
            >
              {recalculating ? 'Starting...' : ACTIVE_RECALC_STATUSES.has(recalculationStatus?.status) ? 'Calculating...' : 'Recalculate All'}
            </button>
          {/if}
        </div>
      </div>

      {#if dashboardError}
        <div class="error-message">{dashboardError}</div>
      {/if}

      {#if dashboardMonth}
        <ClassIncidentReport month={dashboardMonth} refreshKey={incidentRefreshKey} />
      {/if}

      {#if recalculationError}
        <div class="error-message">{recalculationError}</div>
      {/if}

      {#if recalculationSuccess}
        <div class="success-message">
          {recalculationMessage || 'Done.'}
        </div>
      {/if}

      {#if allSnapshotsData}
        <div class="dashboard-summary">
          <h3>Summary for {availableMonths.find(m => m.value === dashboardMonth)?.label}</h3>
          <div class="summary-stats">
            <div class="stat-item">
              <strong>Total Teachers:</strong> {displayedSummary ? displayedSummary.total_teachers : allSnapshotsData.summary.total_teachers}
            </div>
            <div class="stat-item">
              <strong>Grand Total Payment:</strong> €{displayedSummary ? displayedSummary.grand_total_payment : allSnapshotsData.summary.grand_total_payment}
            </div>
            <div class="stat-item">
              <strong>Total Classes:</strong> {displayedSummary ? displayedSummary.total_classes : allSnapshotsData.summary.total_classes}
            </div>
            <div class="stat-item">
              <strong>Average per Teacher:</strong> €{displayedSummary ? displayedSummary.average_per_teacher : allSnapshotsData.summary.average_per_teacher}
            </div>
          </div>
        </div>

        <div class="snapshots-table-container">
          <table class="snapshots-table">
            <thead>
              <tr>
                <th>Teacher Name</th>
                <th>SOLO EUR</th>
                <th># of SOLO</th>
                <th>DUO EUR</th>
                <th># of DUO</th>
                <th>TRIO EUR</th>
                <th># of TRIO</th>
                <th>TOTAL</th>
                <th>Classes</th>
                <th>Bonus</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {#each displayedSnapshots as snapshot}
                <tr class:finalized={snapshot.is_finalized}>
                  <td class="teacher-name">{snapshot.teacher_name}</td>
                  <td class="currency">€{snapshot.solo_eur}</td>
                  <td class="number">{snapshot.solo_count}</td>
                  <td class="currency">€{snapshot.duo_eur}</td>
                  <td class="number">{snapshot.duo_count}</td>
                  <td class="currency">€{snapshot.trio_eur}</td>
                  <td class="number">{snapshot.trio_count}</td>
                  <td class="currency total-cell"><strong>€{snapshot.total_eur}</strong></td>
                  <td class="number">{snapshot.total_classes}</td>
                  <td class="bonus-cell">
                    <TeacherCommentCoveragePopover
                      teacherId={snapshot.teacher_id}
                      teacherName={snapshot.teacher_name}
                      month={dashboardMonth}
                      bonusEligible={snapshot.bonus_eligible}
                      bonusAmount={snapshot.bonus_amount}
                    />
                  </td>
                  <td class="status-cell">
                    {#if snapshot.is_finalized}
                      <span class="status-finalized">FINALIZED</span>
                    {:else}
                      <span class="status-draft">DRAFT</span>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td><strong>TOTALS</strong></td>
                <td colspan="6"></td>
                <td class="currency"><strong>€{displayedSummary ? displayedSummary.grand_total_payment : allSnapshotsData.summary.grand_total_payment}</strong></td>
                <td class="number"><strong>{displayedSummary ? displayedSummary.total_classes : allSnapshotsData.summary.total_classes}</strong></td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      {/if}
    </div>
  {/if}
  <!-- End Dashboard Tab -->

</div>

<style>
  .teacher-performance-rating {
    max-width: 100%;
    margin: 0 auto;
    padding: 2em;
    width: 100%;
  }

  .teacher-performance-rating h2 {
    color: #fab45a;
    margin-bottom: 0.5em;
  }

  .description {
    color: #666;
    margin-bottom: 2em;
    font-size: 1.1em;
  }

  .teacher-selection {
    margin-bottom: 2em;
  }

  .teacher-selection label {
    display: block;
    margin-bottom: 0.5em;
    font-weight: bold;
    color: #333;
  }

  .month-selection {
    margin-top: 1.5em;
  }

  .month-selection label {
    display: block;
    margin-bottom: 0.5em;
    font-weight: bold;
    color: #333;
  }

  .month-dropdown {
    width: 100%;
    padding: 0.75em;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1em;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .month-dropdown:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
  }

  .month-dropdown:hover {
    border-color: #fab45a;
  }

  .input-container {
    position: relative;
    margin-bottom: 1em;
  }

  input[type="text"] {
    width: 100%;
    padding: 0.75em;
    border: 2px solid #ddd;
    border-radius: 8px;
    font-size: 1em;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  input[type="text"]:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #ddd;
    border-top: none;
    border-radius: 0 0 8px 8px;
    max-height: 240px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
    z-index: 10;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  .autocomplete-list li {
    padding: 0;
  }

  .autocomplete-list button {
    width: 100%;
    background: none;
    border: none;
    padding: 0.75em;
    cursor: pointer;
    display: block;
    text-align: left;
    transition: background-color 0.2s;
  }

  .autocomplete-list button:hover {
    background: #f8f9fa;
  }

  .teacher-option {
    display: flex;
    flex-direction: column;
    gap: 0.25em;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
  }

  .teacher-email {
    font-size: 0.9em;
    color: #666;
  }

  .selected-teacher {
    background: #f8f9fa;
    border: 2px solid #fab45a;
    border-radius: 8px;
    padding: 1.5em;
    margin-top: 1em;
  }

  .selected-teacher-info {
    margin-bottom: 1.5em;
  }

  .selected-teacher-info h3 {
    margin: 0 0 1em 0;
    color: #fab45a;
  }

  .selected-teacher-info p {
    margin: 0.5em 0;
    font-size: 1em;
  }

  .action-buttons {
    display: flex;
    gap: 1em;
    flex-wrap: wrap;
  }

  .embedded-auto {
    padding: 0.75em 0;
    color: #666;
    font-size: 0.95em;
    font-style: italic;
  }

  .btn-submit,
  .btn-clear {
    padding: 0.75em 1.5em;
    border: none;
    border-radius: 6px;
    font-size: 1em;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-submit {
    background: #fab45a;
    color: black;
  }

  .btn-submit:hover:not(:disabled) {
    background: #e09a43;
    transform: translateY(-1px);
  }

  .btn-clear {
    background: #6c757d;
    color: white;
  }

  .btn-clear:hover:not(:disabled) {
    background: #545b62;
    transform: translateY(-1px);
  }

  .btn-submit:disabled,
  .btn-clear:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .error-message {
    color: #d32f2f;
    background: #ffebee;
    padding: 1em;
    border-radius: 6px;
    border-left: 4px solid #d32f2f;
    margin-top: 1em;
  }

  .success-message {
    color: #2e7d32;
    background: #e8f5e8;
    padding: 1em;
    border-radius: 6px;
    border-left: 4px solid #2e7d32;
    margin-top: 1em;
  }
  /* Retention data display styles */
  .retention-container {
    margin-top: 2em;
    padding: 15px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
  }

  .retention-container h3 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.2em;
  }

  .retention-summary {
    margin-bottom: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
    text-align: center;
  }

  .retention-rate {
    font-size: 1.3em;
    color: #fab45a;
    margin-bottom: 5px;
  }

  .retention-summary p {
    margin: 0;
    color: #666;
    font-size: 0.9em;
  }

  .month-comparison {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  /* Payment breakdown specific styling */
  .payment-breakdown-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .payment-section {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .payment-section h5 {
    margin: 0 0 12px 0;
    color: #fab45a;
    font-size: 1em;
    border-bottom: 1px solid #ddd;
    padding-bottom: 4px;
  }

  .class-count-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .class-count-list li {
    padding: 6px 0;
    font-size: 0.9em;
    border-bottom: 1px solid #e9ecef;
  }

  .class-count-list li:last-child {
    border-bottom: none;
  }

  .total-classes {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 6px 8px;
    border-radius: 4px;
    margin-top: 6px;
    border: none !important;
  }

  .payment-total-only {
    background-color: #fab45a;
    color: black;
    padding: 12px;
    border-radius: 8px;
    margin-top: 20px;
    text-align: center;
  }

  .payment-total-only p {
    margin: 0;
    font-size: 1.1em;
  }

  .month-data {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .month-data h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1em;
  }

  .month-data ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .month-data li {
    padding: 3px 0;
    font-size: 0.9em;
  }

  .student-details {
    margin-bottom: 15px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  .student-details h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1em;
  }

  .student-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 5px;
  }

  .student-list li {
    padding: 3px 0;
    font-size: 0.9em;
    color: #555;
  }

  /* Comments analysis styles */
  .comments-section {
    margin-bottom: 20px;
  }

  .comments-section h4 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 1.1em;
    border-bottom: 1px solid #ddd;
    padding-bottom: 5px;
  }

  .comments-section h5 {
    margin: 0 0 8px 0;
    color: #fab45a;
    font-size: 1em;
  }

  .metrics-grid {
    display: grid;
    gap: 15px;
    margin-top: 10px;
  }

  .metric-item {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
  }

  .metric-item strong {
    display: block;
    margin-bottom: 8px;
    color: #333;
    font-size: 0.9em;
  }

  .metric-bar {
    width: 100%;
    height: 20px;
    background-color: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 5px;
  }

  .metric-fill {
    height: 100%;
    background-color: #fab45a;
    transition: width 0.3s ease;
    border-radius: 10px;
  }

  .metric-item small {
    color: #666;
    font-size: 0.8em;
  }

  .sample-comments {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .comment-item {
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e9ecef;
  }

  .comment-item:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .comment-item strong {
    color: #fab45a;
    font-size: 0.9em;
    margin-right: 8px;
  }

  .comment-item span {
    color: #555;
    font-size: 0.9em;
    line-height: 1.4;
  }

  .no-data {
    text-align: center;
    padding: 20px;
    color: #666;
    font-style: italic;
    background-color: #f8f9fa;
    border-radius: 6px;
  }

  /* Completion and punctuality specific styles */
  .weekly-trends {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-top: 10px;
  }

  .week-item {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
    text-align: center;
  }

  .week-item strong {
    display: block;
    color: #fab45a;
    margin-bottom: 8px;
    font-size: 0.9em;
  }

  .week-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
  }

  .week-stats span {
    font-size: 0.8em;
    color: #555;
  }

  .week-stats .late-incidents {
    color: #d32f2f;
    font-size: 0.75em;
    font-weight: 500;
  }

  .week-item small {
    color: #666;
    font-size: 0.75em;
  }





  .incident-details {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;
  }

  .incident-details h5 {
    margin: 0 0 10px 0;
    color: #fab45a;
    font-size: 0.95em;
  }

  .incident-item {
    background-color: white;
    padding: 10px;
    margin-bottom: 8px;
    border-radius: 4px;
    border-left: 3px solid #fab45a;
  }

  .incident-item:last-child {
    margin-bottom: 0;
  }

  .incident-item strong {
    display: block;
    color: #333;
    margin-bottom: 4px;
    font-size: 0.9em;
  }

  .incident-item small {
    display: block;
    color: #666;
    font-size: 0.8em;
    margin-bottom: 4px;
  }

  .incident-item span {
    display: block;
    color: #555;
    font-size: 0.85em;
    line-height: 1.4;
  }



  /* Student attendance specific styles */
  .attendance-details {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
  }

  .attendance-item {
    background-color: white;
    padding: 12px;
    margin-bottom: 10px;
    border-radius: 4px;
    border-left: 3px solid #fab45a;
  }

  .attendance-item:last-child {
    margin-bottom: 0;
  }

  .attendance-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .attendance-header strong {
    color: #333;
    font-size: 0.9em;
  }

  .attendance-rate {
    background-color: #fab45a;
    color: black;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .attendance-stats {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .attendance-stats small {
    color: #666;
    font-size: 0.8em;
  }

  .attendance-stats .no-show {
    color: #d32f2f;
    font-weight: 500;
  }

  .more-classes {
    text-align: center;
    padding: 10px;
    color: #666;
    font-style: italic;
  }

  /* Student feedback specific styles */
  .student-feedback {
    border-left-color: #2e7d32;
    background-color: #f8fff8;
  }

  .rating-info {
    display: flex;
    gap: 15px;
    margin-top: 6px;
    flex-wrap: wrap;
  }

  .rating-info small {
    background-color: #e8f5e8;
    padding: 2px 6px;
    border-radius: 4px;
    color: #2e7d32;
    font-size: 0.75em;
  }

  .rating-info .punctuality-issue {
    background-color: #ffebee;
    color: #d32f2f;
    font-weight: 500;
  }

  .more-reports {
    text-align: center;
    padding: 10px;
    color: #666;
    font-style: italic;
    background-color: #f0f8f0;
    border-radius: 4px;
    margin-top: 8px;
  }

  .missing-surveys {
    display: flex;
    flex-direction: column;
    gap: 1em;
  }

  .missing-item {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 1em;
  }

  .missing-item strong {
    color: #dc2626;
  }

  .missing-badge {
    display: inline-block;
    background: #dc2626;
    color: white;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 0.5em;
  }

  .survey-ratings {
    display: flex;
    gap: 1em;
    margin-top: 0.5em;
    flex-wrap: wrap;
  }

  .survey-ratings span {
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.8em;
    color: #0369a1;
  }

  /* Rating color classes */
  .rating-badge {
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-weight: 500;
    display: inline-block;
  }

  /* Override default survey-ratings span styles with rating colors */
  .survey-ratings .rating-badge.rating-1,
  .rating-badge.rating-1 {
    background-color: #ffebee !important;
    border: 1px solid #ef5350 !important;
    color: #c62828 !important;
  }

  .survey-ratings .rating-badge.rating-2,
  .rating-badge.rating-2 {
    background-color: #fff3e0 !important;
    border: 1px solid #ff9800 !important;
    color: #e65100 !important;
  }

  .survey-ratings .rating-badge.rating-3,
  .rating-badge.rating-3 {
    background-color: #fffde7 !important;
    border: 1px solid #ffc107 !important;
    color: #f57f17 !important;
  }

  .survey-ratings .rating-badge.rating-4,
  .rating-badge.rating-4 {
    background-color: #f1f8e9 !important;
    border: 1px solid #8bc34a !important;
    color: #558b2f !important;
  }

  .survey-ratings .rating-badge.rating-5,
  .rating-badge.rating-5 {
    background-color: #e8f5e9 !important;
    border: 1px solid #4caf50 !important;
    color: #2e7d32 !important;
  }

  .class-details {
    margin-top: 0.5em;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
  }

  .class-details p {
    margin: 0.5em 0;
    font-style: italic;
    color: #555;
  }

  .more-classes {
    text-align: center;
    margin-top: 0.5em;
    padding-top: 0.5em;
    border-top: 1px solid #eee;
  }

  .more-classes .expand-button {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 0.9em;
    color: #333;
    transition: all 0.2s ease;
  }

  .more-classes .expand-button:hover {
    background-color: #e8e8e8;
    border-color: #bbb;
  }

  .more-classes .expand-button:active {
    background-color: #ddd;
  }







  @media (max-width: 600px) {
    .teacher-performance-rating {
      padding: 1em;
    }
    
    .action-buttons {
      flex-direction: column;
    }
    
    .btn-submit,
    .btn-clear {
      width: 100%;
    }

    .month-comparison {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .student-list {
      grid-template-columns: 1fr;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
    }

    .comment-item {
      padding: 8px 0;
    }

    .comment-item strong,
    .comment-item span {
      display: block;
      margin-bottom: 4px;
    }

    .weekly-trends {
      grid-template-columns: 1fr;
    }

    .week-stats {
      flex-direction: column;
      gap: 2px;
    }

    .week-stats .late-incidents {
      font-size: 0.7em;
    }

    .incident-item strong,
    .incident-item small,
    .incident-item span {
      font-size: 0.85em;
    }

    .attendance-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    .attendance-stats {
      gap: 1px;
    }

    .attendance-stats small {
      font-size: 0.75em;
    }

    .rating-info {
      flex-direction: column;
      gap: 4px;
    }

    .rating-info small {
      font-size: 0.7em;
    }

    .special-conditions-container {
      flex-direction: column;
      gap: 2px;
    }

    .special-condition,
    .total-surcharge {
      font-size: 0.7em;
      padding: 1px 4px;
    }
  }


    .payment-breakdown-container {
      grid-template-columns: 1fr;
      gap: 15px;
    }

    .payment-section {
      padding: 12px;
    }

    .payment-section h5 {
      font-size: 0.95em;
    }

    .class-count-list li {
      font-size: 0.85em;
      padding: 5px 0;
    }

    .total-classes {
      padding: 5px 6px;
      font-size: 0.85em;
    }

    .payment-total-only {
      padding: 10px;
      margin-top: 15px;
    }

    .payment-total-only p {
      font-size: 1em;
    }

  /* Payment analysis specific styles */
  .paid-class {
    background-color: #fab45a;
    color: black;
  }

  .free-class {
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #2e7d32;
  }

  /* Special conditions styling */
  .special-conditions-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }

  .special-condition {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    display: inline-block;
  }

  .ib-condition {
    background-color: #e3f2fd;
    color: #1976d2;
    border: 1px solid #1976d2;
  }

  .special-needs-condition {
    background-color: #fff3e0;
    color: #f57c00;
    border: 1px solid #f57c00;
  }

  .lithuanian-condition {
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #2e7d32;
  }

  .total-surcharge {
    background-color: #fab45a;
    color: black;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: bold;
    display: inline-block;
    border: 2px solid #e09a43;
  }

  /* Legacy IB class styling for backwards compatibility */
  .ib-class {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 2px;
    display: inline-block;
  }

  .free-reason {
    background-color: #ffebee;
    color: #d32f2f;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    margin-top: 2px;
    display: inline-block;
  }

  .first-time {
    background-color: #fff3e0;
    color: #f57c00;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    margin-top: 2px;
    display: inline-block;
  }

  .retained {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75em;
    margin-top: 2px;
    display: inline-block;
  }

  /* Upcoming classes styling */
  .upcoming-class {
    opacity: 0.7;
    background-color: #f8f9fa;
    border-left: 4px solid #6c757d;
  }

  .upcoming-class:hover {
    opacity: 0.9;
  }

  /* Class status and payment layout */
  .class-status-payment {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .class-status {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: 500;
    text-transform: uppercase;
  }

  .class-status.completed {
    background-color: #d4edda;
    color: #155724;
  }

  .class-status.upcoming {
    background-color: #fff3cd;
    color: #856404;
  }

  /* Teacher Attendance Status Styles */
  .attendance-status {
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 600;
    text-transform: uppercase;
  }

  .attendance-status.attended,
  .attendance-status.on_time {
    background-color: #d4edda;
    color: #155724;
  }

  .attendance-status.late {
    background-color: #fff3cd;
    color: #856404;
  }

  .attendance-status.no_show {
    background-color: #f8d7da;
    color: #721c24;
  }

  /* Student rating summary styles */
  .student-rating-summary {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-top: 15px;
  }

  .rating-display {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 15px;
  }

  .rating-value {
    font-size: 2.5em;
    color: #fab45a;
    font-weight: bold;
    min-width: 120px;
  }

  .rating-details {
    flex: 1;
  }

  .rating-details p {
    margin: 5px 0;
    color: #666;
  }

  .rating-details p:first-child {
    font-weight: bold;
    color: #333;
    font-size: 1.1em;
  }

  .rating-bar-container {
    width: 100%;
  }

  @media (max-width: 600px) {
    .rating-display {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .rating-value {
      font-size: 2em;
      min-width: auto;
    }

    .student-rating-summary {
      padding: 15px;
    }
  }

  .bonus-eligibility-banner {
    margin-top: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    color: black;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .bonus-eligibility-banner.not-eligible {
    border-left: 4px solid #d32f2f;
    background-color: #fff3e0;
  }

  .bonus-icon {
    font-size: 2em;
    color: #fab45a;
  }

  .bonus-message h4 {
    margin: 0 0 5px 0;
    color: #333;
  }

  .bonus-message p {
    margin: 5px 0;
    color: #666;
  }

  .bonus-message small {
    color: #888;
    font-size: 0.9em;
  }

  .criteria-breakdown {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .criterion-item {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
  }

  .criterion-name {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .criterion-status {
    font-size: 1.2em;
    font-weight: bold;
  }

  .criterion-status.met {
    color: #4caf50;
  }

  .criterion-status.not-met {
    color: #d32f2f;
  }

  .criterion-details p {
    margin: 5px 0;
    color: #666;
  }

    .criterion-value {
    font-weight: bold;
    color: #333;
  }

  @media (max-width: 768px) {
    .bonus-eligibility-banner {
      flex-direction: column;
      text-align: center;
      gap: 15px;
    }

    .bonus-icon {
      font-size: 1.5em;
    }
  }

  /* Tab Navigation Styles */
  .tab-navigation {
    display: flex;
    gap: 10px;
    margin-bottom: 2em;
    border-bottom: 2px solid #ddd;
  }

  .tab-button {
    padding: 12px 24px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1em;
    font-weight: 500;
    color: #666;
    border-bottom: 3px solid transparent;
    transition: all 0.2s;
  }

  .tab-button:hover {
    color: #fab45a;
  }

  .tab-button.active {
    color: #fab45a;
    border-bottom-color: #fab45a;
  }

  /* Dashboard Tab Styles */
  .dashboard-tab {
    padding: 1em 0;
  }

  .dashboard-controls {
    margin-bottom: 2em;
  }

  .dashboard-month-selector {
    display: flex;
    gap: 15px;
    align-items: center;
    margin-top: 0.5em;
  }

  .btn-load-dashboard {
    padding: 0.75em 1.5em;
    background: #fab45a;
    color: black;
    border: none;
    border-radius: 6px;
    font-size: 1em;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-load-dashboard:hover:not(:disabled) {
    background: #e09a43;
    transform: translateY(-1px);
  }

  .btn-load-dashboard:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-recalculate-dashboard {
    padding: 0.75em 1.5em;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 1em;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-recalculate-dashboard:hover:not(:disabled) {
    background: #218838;
    transform: translateY(-1px);
  }

  .btn-recalculate-dashboard:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .dashboard-summary {
    background: #f8f9fa;
    padding: 1.5em;
    border-radius: 8px;
    margin-bottom: 2em;
  }

  .dashboard-summary h3 {
    margin: 0 0 1em 0;
    color: #333;
  }

  .summary-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1em;
  }

  .stat-item {
    background: white;
    padding: 1em;
    border-radius: 6px;
    border-left: 3px solid #fab45a;
  }

  .stat-item strong {
    display: block;
    margin-bottom: 0.5em;
    color: #666;
    font-size: 0.9em;
  }

  /* Table Styles */
  .snapshots-table-container {
    overflow-x: visible;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .snapshots-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 1em;
    table-layout: auto;
  }

  .snapshots-table thead {
    background: #fab45a;
    color: black;
  }

  .snapshots-table th {
    padding: 12px 12px;
    text-align: left;
    font-weight: 600;
    border-bottom: 2px solid #e09a43;
  }

  .snapshots-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #e9ecef;
  }

  .snapshots-table tbody tr:hover {
    background: #f8f9fa;
  }

  .snapshots-table tbody tr.finalized {
    background: #e8f5e8;
  }

  .snapshots-table tbody tr.finalized:hover {
    background: #d4edda;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
  }

  .currency {
    text-align: right;
    font-family: 'Courier New', monospace;
  }

  .number {
    text-align: center;
  }

  .total-cell {
    background: #fff3e0;
    font-weight: bold;
  }

  .bonus-cell {
    text-align: center;
  }

  .bonus-yes {
    color: #2e7d32;
    font-weight: 500;
  }

  .bonus-no {
    color: #999;
  }

  .status-cell {
    text-align: center;
  }

  .status-finalized {
    background: #d4edda;
    color: #155724;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: 600;
  }

  .status-draft {
    background: #fff3cd;
    color: #856404;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: 600;
  }

  .snapshots-table tfoot {
    background: #f8f9fa;
    font-weight: bold;
  }

  .totals-row td {
    padding: 15px 8px;
    border-top: 2px solid #fab45a;
  }

</style>
